import { useEffect, useRef, useState } from 'react';

import styles from './PipelineBuilder.module.scss';

import { getDefaultChartColorSchema } from '../../visual-analytics/zoompan-canvas/helpers';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { BlockingOpenedItem } from '../../../components/blocking-opened-item/BlockingOpenedItem';
import NewETLComponentForm from '../new-etl-component-form/newETLComponentForm';
import { TpETLPipeline, TpETLPipelineComponent, TpETLDryRun, TpETLDataContext } from '../interfaces';
import {
  computeStringHash,
  exportPipelineToYaml,
  getDownstreamDependentComponents,
  getETLComponentById,
  importPipelineFromYaml,
} from '../utils';
import { getThemedGrayFraction } from '../../themes/themes';
import { getETLComponentType } from '../etl-component-types/factory';
import { interpolateColors } from '../../visual-analytics/helpers/colors';
import EditETLComponentForm from '../edit-etl-component/editETLComponentForm';
import { performDryRunETL, generateEmptyETLDryRun, getDryRunErrorsForComponent } from '../etlDryRun';
import { promptYamlModal } from '../../../components/yaml-editor/YamlEditorModal';
import { showErrorMessage } from '../../../components/message-boxes/ErrorMessage';
import { useApiContext } from '../../../api';
import { PresignedBlobData } from '../../../api/models/data';
import { promptDataTableModal } from '../../../components/data-table-modal/DataTableModal';
import { TpAssetInfo } from '../../../data/assets/interfaces';
import { analyticsDataStorage } from '../../../data/analyticsDataStorage';
import { buildETLDataContext, dataContextToETLDataContext } from '../dataContext';
import { FancyButton } from '../../../components/primitives/FancyButton';
import { TpETLFlowManifest } from '../etl-flows/interfaces';
import { TpTemplateInfo } from '../../../api/models/assets';
import { getAssetTemplateId } from '../../../data/templateSlice.helpers';
import { DownloadHelper } from './DownloadHelper';
import { getLayoutForPipeline } from './layout';
import { OutputPort } from './OutputPort';

function generateEmptyPipeline(): TpETLPipeline {
  return {
    components: [],
    componentMap: {},
  };
}

function addPipelineComponent(pipeline: TpETLPipeline, newComp: TpETLPipelineComponent) {
  pipeline.components.push(newComp);
  pipeline.componentMap[newComp.id] = newComp;
}

function deletePipelineComponent(pipeline: TpETLPipeline, compId: string) {
  pipeline.components = pipeline.components.filter((comp) => comp.id !== compId);
  delete pipeline.componentMap[compId];
}

interface TpPoint {
  x: number;
  y: number;
}

interface TpLayoutedGraph {
  height: number;
  width: number;
  children: {
    id: string;
    x: number;
    y: number;
    width: number;
    height: number;
  }[];
  edges: {
    id: string;
    sourcePort?: string;
    sections: {
      startPoint: TpPoint;
      endPoint: TpPoint;
      bendPoints?: TpPoint[];
    }[];
  }[];
}

function getOfssetInDir(pt1: TpPoint, pt2: TpPoint, size: number, sizeOrtho?: number): TpPoint {
  // Returns an offset of size `size`, starting from `pt1`m in the direction of `pt2`
  // `sizeOrtho` adds an optional additional distance, orthoginal to that direction
  const dirX = pt2.x - pt1.x;
  const dirY = pt2.y - pt1.y;
  const dirSize = Math.sqrt(dirX ** 2 + dirY ** 2);
  const pt = {
    x: pt1.x + (dirX / dirSize) * size,
    y: pt1.y + (dirY / dirSize) * size,
  };
  if (sizeOrtho) {
    pt.x += (dirY / dirSize) * sizeOrtho;
    pt.y -= (dirX / dirSize) * sizeOrtho;
  }
  return pt;
}

function dist(pt1: TpPoint, pt2: TpPoint): number {
  // Returns the distance between two points
  const dirX = pt2.x - pt1.x;
  const dirY = pt2.y - pt1.y;
  return Math.sqrt(dirX ** 2 + dirY ** 2);
}

function drawPlumbing(
  ctx: any,
  xL2S: (x: number) => number,
  yL2S: (x: number) => number,
  points: { x: number; y: number }[],
) {
  // Draws a connection between two nodes, with a number of orthogonal bends
  ctx.beginPath();
  ctx.moveTo(xL2S(points[0].x), yL2S(points[0].y));
  for (let idx = 1; idx < points.length; idx++) {
    const ptPrev = points[idx - 1];
    const ptCurr = points[idx];
    if (idx < points.length - 1) {
      const ptNext = points[idx + 1];
      let radius = 25; // bends are drawn with a round corner, with a specified radius.
      // However, this radius should not be larged than half the size of the smallest segment
      if (idx > 1) radius = Math.min(radius, dist(ptPrev, ptCurr) / 2);
      else radius = Math.min(radius, dist(ptPrev, ptCurr));
      if (idx < points.length - 2) radius = Math.min(radius, dist(ptNext, ptCurr) / 2);
      else radius = Math.min(radius, dist(ptNext, ptCurr));
      const pt1 = getOfssetInDir(ptCurr, ptPrev, radius);
      const pt2 = getOfssetInDir(ptCurr, ptNext, radius);
      ctx.lineTo(xL2S(pt1.x), yL2S(pt1.y));
      ctx.bezierCurveTo(xL2S(ptCurr.x), xL2S(ptCurr.y), xL2S(ptCurr.x), xL2S(ptCurr.y), xL2S(pt2.x), xL2S(pt2.y));
      ctx.lineTo(xL2S(pt2.x), yL2S(pt2.y));
    } else {
      ctx.lineTo(xL2S(ptCurr.x), yL2S(ptCurr.y));
    }
  }
  ctx.stroke();

  // Draw the arrow at the end
  ctx.beginPath();
  const ptPrev = points[points.length - 2];
  const ptCurr = points[points.length - 1];
  const pt1 = getOfssetInDir(ptCurr, ptPrev, 7, 3);
  const pt2 = getOfssetInDir(ptCurr, ptPrev, 7, -3);
  ctx.moveTo(xL2S(ptCurr.x), yL2S(ptCurr.y));
  ctx.lineTo(xL2S(pt1.x), yL2S(pt1.y));
  ctx.lineTo(xL2S(pt2.x), yL2S(pt2.y));
  ctx.closePath();
  ctx.fill();
}

interface TpPipelineBuilderProps {
  saveAssetPipeline?: (pipelineConfig: string) => Promise<void>;
  assetPipelineConfig?: string;
  assetInfo: TpAssetInfo | null; // Null for PipelineBuilder that does not belong to an asset
  executePipeline?: (pipelineConfig: string, modifiedComponents?: string[]) => Promise<void>;
  lastRunConfig?: string;
  reportLink?: string;
  lastComponentProgress?: { [id: string]: any };
  runningComponentProgress?: { [id: string]: any };
}

function PipelineBuilder(props: TpPipelineBuilderProps) {
  const [assetETLDataContext, setAssetETLDataContext] = useState<TpETLDataContext | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [pipeline, setPipeline] = useState<TpETLPipeline>(generateEmptyPipeline());
  const [pipelineImported, setPipelineImported] = useState(false);
  const [dryRun, setDryRun] = useState<TpETLDryRun>(generateEmptyETLDryRun());
  const [selectedOutputIds, setSelectedOutputIds] = useState<string[]>([]);
  const [editingComponent, setEditingComponent] = useState<TpETLPipelineComponent | null>(null);
  const [promptAddComponent, setPromptAddComponent] = useState(false);
  const [layoutedGraph, setLayoutedGraph] = useState<any>(null);
  const [canvasDim, setCanvasDim] = useState({ width: 20, height: 20 });
  const [componentNodes, setComponentNodes] = useState<any[]>([]);
  const [modifiedComponents, setModifiedComponents] = useState<string[]>([]);
  const [runningComponents, setRunningComponents] = useState<string[]>([]);
  const [failedComponents, setFailedComponents] = useState<string[]>([]);
  const [completedComponents, setCompletedComponents] = useState<string[]>([]);
  const [queuedComponents, setQueuedComponents] = useState<string[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const refEdgesDraw = useRef<HTMLCanvasElement>(null);
  const api = useApiContext();

  const downloadHelper = new DownloadHelper(props.lastComponentProgress, props.runningComponentProgress);

  async function fetchDataContext() {
    if (!props.assetInfo) return null;
    if (!props.assetInfo.properties?.['data_context']) return null;
    const dataContextId = props.assetInfo.properties['data_context'];

    // Fetch from the analyticsDataStorage
    if (analyticsDataStorage.dataContexts[dataContextId]) {
      const dataContext = analyticsDataStorage.dataContexts[dataContextId];
      const etlDataContext = dataContextToETLDataContext(dataContext, props.assetInfo);
      return etlDataContext;
    }

    // Fetch from the API
    const response = await api.dataContext.getDatacontextData(dataContextId, props.assetInfo);
    if (response === null) {
      return null;
    } else {
      const etlDataContext = await buildETLDataContext(response, props.assetInfo);
      return etlDataContext;
    }
  }

  function toggleSelectOutput(outputId: string) {
    setSelectedOutputIds((previousSelection) => {
      if (previousSelection.includes(outputId)) {
        return previousSelection.filter((id) => id != outputId);
      } else {
        return [...previousSelection, outputId];
      }
    });
  }

  function updatePipelineTemplate(flow: TpETLFlowManifest) {
    setLayoutedGraph(null);
    setEditingComponent(null);
    setPipeline(generateEmptyPipeline());
    setPromptAddComponent(false);
    flow.getPipeline().then((pipeline) => {
      updatePipeline(pipeline);
      if (props.assetInfo) {
        if (flow.dashboardTemplate) {
          const template: TpTemplateInfo = {
            name: props.assetInfo.name,
            displayName: props.assetInfo.displayName,
            scope: 'dashboard',
            templateType: props.assetInfo.type,
            yamlString: flow.dashboardTemplate,
            isActive: true,
            isSystem: false,
            isDefault: true,
            assetId: props.assetInfo.id,
            id: getAssetTemplateId(props.assetInfo.id, 'dashboard'),
          };
          api.asset.modifyAssetTemplate(props.assetInfo.id, template);
        }
        if (flow.entityTemplate) {
          const template: TpTemplateInfo = {
            name: props.assetInfo.name,
            displayName: props.assetInfo.displayName,
            scope: 'entity',
            templateType: props.assetInfo.type,
            yamlString: flow.entityTemplate,
            isActive: true,
            isSystem: false,
            isDefault: true,
            assetId: props.assetInfo.id,
            id: getAssetTemplateId(props.assetInfo.id, 'entity'),
          };
          api.asset.modifyAssetTemplate(props.assetInfo.id, template);
        }
      }
    });
  }

  function updatePipeline(pipeline: TpETLPipeline) {
    const newPipelineObj = { ...pipeline };
    setDryRun(performDryRunETL(newPipelineObj, assetETLDataContext));
    setPipeline(newPipelineObj);
  }

  function downloadFile(url: string) {
    const link = document.createElement('a');
    link.href = url;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  function downloadBlobPath(blobPath: string) {
    const blobData: PresignedBlobData = {
      method: 'GET',
      blobPath: blobPath,
      isDownload: true,
    };
    api.blob
      .getPresignedURL(blobData)
      .then((response) => {
        if (!response.signedUrl) {
          showErrorMessage('Failed to get signed URL');
          return;
        }
        downloadFile(response.signedUrl);
      })
      .catch((error) => {
        if (error != null) {
          console.error(`There was a problem while fetching the data: ${String(error)}`);
          showErrorMessage(String(error));
        }
      });
  }

  function downloadLogResults(comp: TpETLPipelineComponent) {
    const compProgress = downloadHelper.getProgressLogs(comp.id);
    const url = compProgress.log_path;
    downloadBlobPath(url);
  }

  function downloadLastResults(comp: TpETLPipelineComponent) {
    downloadHelper.getProgressResultPath(comp, getETLComponentType(comp.componentType)).then((url) => {
      if (url === null) {
        return Promise.reject(null);
      }
      return downloadBlobPath(url);
    });
  }

  function downloadReport() {
    if (!props.reportLink) {
      showErrorMessage('Report not available');
      return;
    }
    downloadBlobPath(props.reportLink);
  }

  function showLastResults(comp: TpETLPipelineComponent) {
    let splitByTab = false;
    downloadHelper
      .getProgressResultPath(comp, getETLComponentType(comp.componentType))
      .then((url) => {
        if (url === null) {
          return Promise.reject(null);
        }
        const blobData: PresignedBlobData = {
          method: 'GET',
          blobPath: url,
        };
        return api.blob.getPresignedURL(blobData);
      })
      .then((result) => {
        const presignedUrl = result.signedUrl;
        splitByTab = result.blobPath!.endsWith('.tsv');
        if (presignedUrl) {
          return fetch(presignedUrl).then((response) => {
            if (response.ok) return response.text();
            throw new Error('Network response was not ok.');
          });
        } else {
          showErrorMessage('Error: Unable to load Table. Data not Found.');
        }
      })
      .then((tsv) => {
        if (tsv) promptDataTableModal(tsv, splitByTab);
      })
      .catch((error) => {
        if (error != null) {
          // Null thrown on cancel or no output path
          console.error(`There was a problem while fetching the data: ${String(error)}`);
          showErrorMessage(String(error));
        }
      });
  }

  function addComponent(newComp: TpETLPipelineComponent) {
    addPipelineComponent(pipeline, newComp);
    updatePipeline(pipeline);
    setSelectedOutputIds([...Object.values(newComp.outputObjectIds)]);
    setTimeout(() => {
      // refresh the layout
      calcLayout();
    }, 50);
    setPromptAddComponent(false);
    setTimeout(() => {
      // automatically open the added component
      setEditingComponent(getETLComponentById(pipeline, newComp.id));
    }, 50);
  }

  function updateComponent(updatedComp: TpETLPipelineComponent) {
    const compIdx = pipeline.components.findIndex((cmp) => cmp.id == updatedComp.id);
    if (compIdx < 0) throw `Component not found: ${updatedComp.id}`;
    pipeline.components[compIdx] = updatedComp;
    pipeline.componentMap[updatedComp.id] = updatedComp;
    updatePipeline(pipeline);
    setEditingComponent(null);
  }

  function deleteComponent(compId: string) {
    // Deleting a component also automatically delete all components that depend on it
    const toDeleteComponents = [
      getETLComponentById(pipeline, compId),
      ...getDownstreamDependentComponents(pipeline, compId),
    ];
    for (const comp of toDeleteComponents) deletePipelineComponent(pipeline, comp.id);
    updatePipeline(pipeline);
    setSelectedOutputIds([]);
    setLayoutedGraph(null);
    setTimeout(() => {
      calcLayout();
    }, 50);
    setEditingComponent(null);
  }

  const pixelRatio = 2;

  const xL2S = (x: number) => pixelRatio * x;
  const yL2S = (y: number) => pixelRatio * y;

  const colorSchema = getDefaultChartColorSchema();

  function renderLayoutedGraphEdges(layoutedGraph: TpLayoutedGraph) {
    // The edges of the graph are rendered on a canvas element underneath the components, which are rendered as divs with absolute positioning
    if (!layoutedGraph) return;
    const canvasDraw = refEdgesDraw.current;
    const contextDraw = canvasDraw?.getContext('2d');
    if (!canvasDraw || !contextDraw) return;
    contextDraw.fillStyle = colorSchema.backColor.toCanvasString();
    contextDraw.fillRect(0, 0, canvasDraw.width, canvasDraw.height);

    for (const edge of layoutedGraph.edges) {
      for (let ctr = 0; ctr <= 1; ctr++) {
        if (ctr == 0) {
          // First render: with a with, broader stroke, to make nice crossings
          contextDraw.strokeStyle = getThemedGrayFraction(1).toCanvasString();
          contextDraw.fillStyle = getThemedGrayFraction(1).toCanvasString();
          contextDraw.lineWidth = 10;
        } else {
          // Second render: actual lines
          contextDraw.strokeStyle = getThemedGrayFraction(0.5).toCanvasString();
          contextDraw.fillStyle = getThemedGrayFraction(0.5).toCanvasString();
          contextDraw.lineWidth = 2;
          contextDraw.setLineDash([]);
          if (edge.id.startsWith('extraParam_')) contextDraw.setLineDash([4, 4]);
        }
        for (const section of edge.sections) {
          if (section.bendPoints) {
            drawPlumbing(contextDraw, xL2S, yL2S, [section.startPoint, ...section.bendPoints!, section.endPoint]);
          } else {
            drawPlumbing(contextDraw, xL2S, yL2S, [section.startPoint, section.endPoint]);
          }
        }
      }
    }
  }

  function calcLayout() {
    getLayoutForPipeline(pipeline).then((layoutedGraphInp) => {
      const theLayoutedGraph = layoutedGraphInp as TpLayoutedGraph;
      setCanvasDim({ width: theLayoutedGraph.width, height: theLayoutedGraph.height });
      setLayoutedGraph(theLayoutedGraph);
    });
  }
  useEffect(() => {
    calcLayout();
  }, [runningComponents]);

  function setComponentStatuses() {
    // This function sets the status of the components, based on the last run and the current run
    let newModifiedComponents: string[] = [];
    let completedComps: string[] = [];
    const runningComps: string[] = [];
    const queuedComps: string[] = [];
    let failedComps: string[] = [];

    // If this is the first run, all the components are modified
    // Otherwise determine modified components based on last run
    if (props.lastRunConfig) {
      const lastRunPipeline = importPipelineFromYaml(props.lastRunConfig);
      const componentHashes = new Set(lastRunPipeline.components.map((comp) => computeStringHash(comp)));

      // Find components that have changed or are new
      for (const component of pipeline.components) {
        if (!componentHashes.has(computeStringHash(component))) {
          newModifiedComponents.push(component.id);
        }
      }

      // Add downstream dependencies
      for (const modifiedComponentId of [...newModifiedComponents]) {
        const dependents = getDownstreamDependentComponents(pipeline, modifiedComponentId);
        for (const depComp of dependents) {
          if (!newModifiedComponents.includes(depComp.id)) {
            newModifiedComponents.push(depComp.id);
          }
        }
      }
    } else {
      newModifiedComponents = pipeline.components.map((comp) => comp.id);
    }

    // Process completed components from last successful run
    if (props.lastComponentProgress) {
      Object.entries(props.lastComponentProgress).forEach(([compId, compProgress]) => {
        if (compProgress.status === 'completed' && !newModifiedComponents.includes(compId)) {
          completedComps.push(compId);
        } else if (compProgress.status === 'failed') {
          newModifiedComponents.push(compId);
          failedComps.push(compId);
        } else {
          newModifiedComponents.push(compId);
        }
      });
    }

    // Process current run status
    if (props.runningComponentProgress) {
      setIsRunning(true);
      Object.entries(props.runningComponentProgress).forEach(([compId, compProgress]) => {
        failedComps = failedComps.filter((id) => id !== compId);
        completedComps = completedComps.filter((id) => id !== compId);
        switch (compProgress.status) {
          case 'running':
            runningComps.push(compId);
            break;
          case 'completed':
            completedComps.push(compId);
            newModifiedComponents = newModifiedComponents.filter((id) => id !== compId);
            break;
          case 'failed':
            failedComps.push(compId);
            break;
          case 'queued':
            queuedComps.push(compId);
            break;
        }
      });
    } else {
      setIsRunning(false);
    }

    setModifiedComponents([...new Set(newModifiedComponents)]);
    setCompletedComponents(completedComps);
    setFailedComponents(failedComps);
    setRunningComponents(runningComps);
    setQueuedComponents(queuedComps);
  }

  useEffect(() => {
    setComponentStatuses();
  }, [pipeline, props.runningComponentProgress, props.lastRunConfig]);

  useEffect(() => {
    if (isRunning) return; // don't fetch data context if the pipeline is running
    setIsLoading(true);
    fetchDataContext()
      .then((etlDataContext) => {
        setAssetETLDataContext(etlDataContext);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [isRunning]);

  useEffect(() => {
    if (!pipelineImported) {
      renderLayoutedGraphEdges(layoutedGraph);
      if (props.assetPipelineConfig) {
        try {
          updatePipeline(importPipelineFromYaml(props.assetPipelineConfig));
        } catch (error) {
          showErrorMessage(String(error));
        }
      }
      setPipelineImported(true);
    } else {
      setDryRun(performDryRunETL(pipeline, assetETLDataContext));
    }
  }, [assetETLDataContext]);

  useEffect(() => {
    // Creating the list of component elements
    const margin = 3; // this margin causes the component box to be slightly offset from the connecting lines, creating a nicer visual effect
    let compNodes = null;
    if (layoutedGraph && pipeline) {
      compNodes = layoutedGraph.children.map((layoutComp: any) => {
        const comp = getETLComponentById(pipeline, layoutComp.id);
        const hasErrors = getDryRunErrorsForComponent(dryRun, comp.id).length > 0;
        const compType = getETLComponentType(comp.componentType);
        const isModified = modifiedComponents.includes(comp.id);
        const isFailed = failedComponents.includes(comp.id);
        const isCompleted = completedComponents.includes(comp.id);
        const compIsRunning = runningComponents.includes(comp.id);
        const compIsQueued = queuedComponents.includes(comp.id);
        let color = getThemedGrayFraction(0.9);
        if (compType.color) color = interpolateColors(getThemedGrayFraction(1), compType.color, 0.8);
        return (
          <div
            key={layoutComp.id}
            className={isModified ? styles.modifiedComponentNode : styles.componentNode}
            style={{
              left: `${layoutComp.x + margin}px`,
              top: `${layoutComp.y + margin}px`,
              width: `${layoutComp.width - 2 * margin}px`,
              height: `${layoutComp.height - 2 * margin}px`,
              backgroundColor: color.toCanvasString(),
            }}
            onClick={(ev) => {
              ev.stopPropagation();
              setEditingComponent(comp);
            }}
          >
            <div className={styles.compName}>{comp.name}</div>
            <div className={styles.compIcon}>{compType.icon}</div>
            {compIsQueued && (
              <div className={styles.compIsQueued}>
                <FontAwesomeIcon icon={['fas', 'hourglass-half']} flip style={{ animationDuration: '3s' }} />
              </div>
            )}
            {compIsRunning && (
              <div className={styles.compIsRunning}>
                <FontAwesomeIcon icon={['fas', 'spinner']} spin />
              </div>
            )}
            {isCompleted && (
              <div className={styles.compIsRunning}>
                <FontAwesomeIcon icon={['fas', 'check']} />
              </div>
            )}
            {isFailed && (
              <div className={styles.compIsFailed}>
                <FontAwesomeIcon icon={['fas', 'times']} />
              </div>
            )}
            {hasErrors && (
              <div className={styles.compError}>
                <FontAwesomeIcon className={styles.abortIcon} icon={['fas', 'triangle-exclamation']} />
              </div>
            )}
            {layoutComp.ports.map((port: any) => (
              <OutputPort
                key={port.id}
                port={port}
                onSelect={toggleSelectOutput}
                isConnected={layoutedGraph.edges.some((edge: any) => edge.sourcePort === port.id)}
                selectionIndex={selectedOutputIds.indexOf(port.properties.outputId)}
                className={isModified ? styles.modified : ''}
                styles={{ backgroundColor: color.toCanvasString() }}
                margin={margin}
                showSelectionNumber={selectedOutputIds.length > 1}
              />
            ))}
          </div>
        );
      });
      setComponentNodes(compNodes);
      renderLayoutedGraphEdges(layoutedGraph);
    }
  }, [layoutedGraph, pipeline, selectedOutputIds, dryRun, isRunning]);

  return (
    <>
      <div className={styles.wrapper} id="blako">
        <div className={styles.header}>
          <div>
            <button
              className={styles.headerIconButton}
              onClick={() => {
                promptYamlModal(exportPipelineToYaml(pipeline)).then((yamlString) => {
                  try {
                    setSelectedOutputIds([]);
                    setLayoutedGraph(null);
                    updatePipeline(importPipelineFromYaml(yamlString));
                  } catch (error: any) {
                    const message = error.userError && error.message ? error.message : String(error);
                    showErrorMessage(message);
                  }
                });
              }}
            >
              <FontAwesomeIcon icon={['fas', 'code']} />
            </button>
            {!!props.saveAssetPipeline && (
              <button
                className={
                  (!!props.lastRunConfig && isRunning) || saveSuccess || isLoading
                    ? styles.disabledHeaderIconButton
                    : styles.headerIconButton
                }
                disabled={(!!props.lastRunConfig && isRunning) || saveSuccess || isLoading}
                onClick={() => {
                  if (props.saveAssetPipeline) {
                    props.saveAssetPipeline(exportPipelineToYaml(pipeline)).then(() => {
                      setSaveSuccess(true);
                      setTimeout(() => {
                        setSaveSuccess(false);
                      }, 5000);
                    });
                  }
                }}
              >
                <FontAwesomeIcon icon={['fas', 'floppy-disk-pen']} />
              </button>
            )}
            {!!props.executePipeline && (
              <button
                className={
                  isLoading || (!!props.lastRunConfig && modifiedComponents.length == 0)
                    ? styles.disabledHeaderIconButton
                    : styles.headerIconButton
                }
                disabled={isLoading || (!!props.lastRunConfig && modifiedComponents.length == 0)}
                onClick={() => {
                  if (props.executePipeline) {
                    let compIds = modifiedComponents;
                    setIsRunning(true);
                    setIsStarting(true);
                    setFailedComponents([]);
                    setQueuedComponents([...compIds]);
                    if (modifiedComponents.length === pipeline.components.length) {
                      compIds = [];
                    }
                    props.executePipeline(exportPipelineToYaml(pipeline), compIds).then(() => {
                      setTimeout(() => {
                        setIsStarting(false);
                      }, 3500);
                    });
                  }
                }}
              >
                <FontAwesomeIcon icon={['fas', 'play']} />
              </button>
            )}
            {!!props.executePipeline && (
              <button
                className={props.reportLink && !isRunning ? styles.headerIconButton : styles.disabledHeaderIconButton}
                disabled={!props.reportLink || isRunning}
                onClick={downloadReport}
                title={props.reportLink ? 'Download PDF Report' : 'Report not available'}
              >
                <FontAwesomeIcon icon={['fas', 'file-pdf']} />
              </button>
            )}
          </div>
          {!!saveSuccess && (
            <div className={styles.savedMessage}>
              <FontAwesomeIcon icon={['fas', 'check']} style={{ marginRight: 10 }} />
              Pipeline Saved
            </div>
          )}
          {!!isRunning && !isStarting && (
            <div>
              <FontAwesomeIcon icon={['fas', 'diagram-sankey']} fade style={{ marginRight: 10 }} />
              Running Workflow Manager ...
            </div>
          )}
        </div>
        {pipeline.components.length === 0 &&
          (isLoading ? (
            <div className={styles.spinnerWrapper}>
              <div className={styles.loadingContent}>
                <FontAwesomeIcon icon={['fas', 'spinner']} spin />
                Loading Workflow ...
              </div>
            </div>
          ) : (
            <div className={styles.emptyState}>
              <div style={{ textAlign: 'center', paddingTop: '40px' }}>
                <FancyButton onClick={() => setPromptAddComponent(true)} iconName="diagram-sankey">
                  Start Creating your Pipeline
                </FancyButton>
              </div>
            </div>
          ))}
        {pipeline.components.length > 0 && (
          <div
            className={isLoading || isStarting ? styles.clientAreaLoading : styles.clientArea}
            onClick={() => {
              // clicking anywhere not on a component clears the selection
              setSelectedOutputIds([]);
            }}
          >
            {isStarting && (
              <div className={styles.loadingContent}>
                <FontAwesomeIcon icon={['fas', 'spinner']} spin />
                Starting Workflow Manager ...
              </div>
            )}
            {isLoading && (
              <div className={styles.loadingContent}>
                <FontAwesomeIcon icon={['fas', 'spinner']} spin />
                Loading DataTables ...
              </div>
            )}
            <canvas
              ref={refEdgesDraw}
              style={{ width: `${canvasDim.width}px`, height: `${canvasDim.height}px` }}
              width={pixelRatio * canvasDim.width}
              height={pixelRatio * canvasDim.height}
            />
            {componentNodes}
          </div>
        )}
        {!isLoading && (
          <div
            className={styles.addButton}
            onClick={(ev) => {
              ev.stopPropagation();
              setPromptAddComponent(true);
            }}
          >
            <FontAwesomeIcon icon={['fas', 'plus']} />
          </div>
        )}
      </div>
      {promptAddComponent && (
        <BlockingOpenedItem
          fraction={70}
          close={() => {
            setPromptAddComponent(false);
          }}
        >
          <NewETLComponentForm
            pipeline={pipeline}
            selectedOutputIds={selectedOutputIds}
            addComponent={addComponent}
            assetETLDataContext={assetETLDataContext}
            updatePipeline={updatePipelineTemplate}
          />
        </BlockingOpenedItem>
      )}
      {editingComponent && (
        <BlockingOpenedItem
          fraction={70}
          close={() => {
            setEditingComponent(null);
          }}
        >
          <EditETLComponentForm
            inpDryRun={dryRun}
            inpComponent={editingComponent}
            selectedOutputIds={selectedOutputIds}
            updateComponent={(newComp) => updateComponent(newComp)}
            deleteComponent={() => {
              deleteComponent(editingComponent.id);
            }}
            showLastResults={
              downloadHelper.getProgressResult(editingComponent.id) ? () => showLastResults(editingComponent) : null
            }
            showLastLogResults={
              downloadHelper.getProgressLogs(editingComponent.id) ? () => downloadLogResults(editingComponent) : null
            }
            downloadLastResults={
              downloadHelper.getProgressResult(editingComponent.id) ? () => downloadLastResults(editingComponent) : null
            }
            assetETLDataContext={assetETLDataContext}
          />
        </BlockingOpenedItem>
      )}
    </>
  );
}

export default PipelineBuilder;
